import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Table, Input, message } from 'antd';
import { Consignment, ConsignmentItem } from '../../model/consignment';

interface Props {
    open: boolean;
    item: Consignment | null;
    onClose: () => void;
    onSettle: (consignmentId: number, note?: string) => Promise<void>;
}

interface PayableItem extends ConsignmentItem {
    unpaidQuantity: number;
    paymentAmount: number;
}

const money = (value: number) => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

export const getPayableItems = (items: ConsignmentItem[] = []): PayableItem[] => items
    .map(item => {
        const sold = Number(item.quantity_sold || 0);
        const settled = Number(item.quantity_settled || 0);
        const unpaidQuantity = Number(item.unpaid_sold_quantity ?? Math.max(0, sold - settled));
        const paymentAmount = Number(
            item.unpaid_owner_payable ?? unpaidQuantity * Number(item.owner_cost_per_unit || 0)
        );
        return { ...item, unpaidQuantity, paymentAmount };
    })
    .filter(item => item.unpaidQuantity > 0);

export const ConsignmentSettleModal: React.FC<Props> = ({
    open,
    item,
    onClose,
    onSettle
}) => {
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) setNote('');
    }, [open, item?.id]);

    const payableItems = useMemo(() => getPayableItems(item?.items || []), [item]);
    const totalQuantity = payableItems.reduce((sum, line) => sum + line.unpaidQuantity, 0);
    const totalAmount = payableItems.reduce((sum, line) => sum + line.paymentAmount, 0);

    if (!item) return null;

    const handleConfirm = async () => {
        if (totalQuantity <= 0) {
            message.error('ไม่มีรายการขายที่ค้างจ่ายในรอบนี้');
            return;
        }
        try {
            setLoading(true);
            await onSettle(item.id, note.trim() || undefined);
            onClose();
        } catch (e: any) {
            message.error(e?.message || 'บันทึกการจ่ายรอบนี้ไม่สำเร็จ');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'สินค้า / ล็อต',
            key: 'product',
            render: (_: any, r: PayableItem) => (
                <div>
                    <div style={{ fontWeight: 700 }}>{r.product_name}</div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>
                        {r.product_code || '-'} · ล็อตที่ {r.batch_no || 1}
                    </div>
                </div>
            )
        },
        {
            title: 'ขายสะสม',
            dataIndex: 'quantity_sold',
            key: 'quantity_sold',
            width: 85,
            align: 'right' as const
        },
        {
            title: 'จ่ายแล้ว',
            dataIndex: 'quantity_settled',
            key: 'quantity_settled',
            width: 80,
            align: 'right' as const,
            render: (value: number) => Number(value || 0)
        },
        {
            title: 'จ่ายรอบนี้',
            dataIndex: 'unpaidQuantity',
            key: 'unpaidQuantity',
            width: 90,
            align: 'right' as const,
            render: (value: number) => <strong style={{ color: '#2563EB' }}>{value}</strong>
        },
        {
            title: 'ต้นทุน/หน่วย',
            dataIndex: 'owner_cost_per_unit',
            key: 'owner_cost_per_unit',
            width: 105,
            align: 'right' as const,
            render: (value: number) => `฿${money(value)}`
        },
        {
            title: 'จำนวนเงิน',
            dataIndex: 'paymentAmount',
            key: 'paymentAmount',
            width: 115,
            align: 'right' as const,
            render: (value: number) => <strong style={{ color: '#DC2626' }}>฿{money(value)}</strong>
        }
    ];

    return (
        <Modal
            open={open}
            title={`💵 จ่ายรอบนี้: ${item.document_no}`}
            onCancel={onClose}
            footer={[
                <Button key="back" onClick={onClose}>ยกเลิก</Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    disabled={totalQuantity <= 0}
                    onClick={handleConfirm}
                    data-testid="btn-confirm-settlement"
                >
                    ยืนยันจ่ายรอบนี้ ฿{money(totalAmount)}
                </Button>
            ]}
            width={780}
            destroyOnClose
        >
            <div style={{ marginBottom: 14 }}>
                <strong>ผู้รับเงิน: {item.vendor_name || '-'}</strong>
                {item.current_period && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#64748B' }}>รอบบิลที่ {item.current_period.period_no}</span>
                )}
                <div style={{ color: '#64748B', fontSize: 13 }}>
                    "จ่ายรอบนี้" ชำระเฉพาะยอดขายที่ยังไม่เคยจ่าย โดย<strong>รอบบิลปัจจุบันยังคงเปิดอยู่</strong> ฝากเพิ่ม/ขายต่อในรอบบิลเดิมได้ตามปกติ
                    (ต่างจาก "ปิดรอบบิล" ที่จะปิดรอบบิลนี้และยกยอดคงเหลือไปเปิดรอบบิลใหม่)
                </div>
            </div>

            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ color: '#1E40AF', fontSize: 12 }}>ยอดจ่ายรอบนี้ ({totalQuantity} ชิ้น)</div>
                <div style={{ color: '#1D4ED8', fontSize: 28, fontWeight: 900 }}>฿{money(totalAmount)}</div>
            </div>

            <Table
                columns={columns}
                dataSource={payableItems.map((line, index) => ({ ...line, key: line.id || index }))}
                locale={{ emptyText: 'ไม่มีรายการขายที่ค้างจ่าย' }}
                pagination={false}
                size="small"
                bordered
                style={{ marginBottom: 16 }}
            />

            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                หมายเหตุ / หลักฐานการจ่าย
            </label>
            <Input
                placeholder="เช่น โอนเงินเข้าบัญชี หรือจ่ายเงินสดหน้าร้าน"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                data-testid="input-settle-note"
            />
        </Modal>
    );
};

export default ConsignmentSettleModal;
